/* oxlint-disable jsx-a11y/label-has-associated-control, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions, next/no-html-link-for-pages */
import { useEffect, useMemo, useState } from "react";
import ProgramsView from "./ProgramsView";
import type { ApplicationProgram } from "./program-types";
import {
  ArrowUpRight,
  BookOpenText,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clipboard,
  Database,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Inbox,
  LayoutDashboard,
  Languages,
  Link2,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Mail,
  Menu,
  MessageSquareText,
  MoreHorizontal,
  PencilLine,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Save,
  Search,
  Send,
  SkipForward,
  Settings2,
  Sparkles,
  Star,
  Trash2,
  Upload,
  UserRoundSearch,
  X,
} from "lucide-react";

type View = "dashboard" | "advisors" | "letters" | "tracking" | "programs" | "settings";

type Advisor = {
  id: string;
  tier: string;
  batch: string;
  institution: string;
  name: string;
  favorite?: boolean;
  title: string;
  email: string;
  status: string;
  profileUrl: string;
  plannedSendDate: string;
  actualSentDate: string;
  nextFollowUp: string;
  replyStatus: string;
  notes: string;
  coreDirection: string;
  recentWork: string;
  keyMethods: string;
  keyFinding: string;
  fit: string;
  extensionQuestion: string;
  platformAdvantage: string;
  proposedQuestion: string;
  personalization: string;
  riskNote: string;
  sourceUrl: string;
};

type Letter = {
  advisorId: string;
  chineseSubject: string;
  chineseBody: string;
  subject: string;
  body: string;
  status: string;
  version: number;
  updatedAt?: string;
};

type OutreachEvent = {
  id: string;
  advisorId: string;
  type: string;
  occurredAt: string;
  note: string;
  nextAction: string;
  nextActionAt: string;
  createdAt: string;
};

type AppData = {
  advisors: Advisor[];
  letters: Letter[];
  events: OutreachEvent[];
  programs?: ApplicationProgram[];
  exportedAt?: string;
};

type AiLetterResult = {
  subject: string;
  body: string;
  changeSummary: string[];
  warnings: string[];
  promptVersion: string;
  model: string;
};

const navItems: Array<{ id: View; label: string; icon: typeof LayoutDashboard }> = [
  { id: "dashboard", label: "总览", icon: LayoutDashboard },
  { id: "advisors", label: "导师信息", icon: UserRoundSearch },
  { id: "letters", label: "套磁信", icon: FileText },
  { id: "tracking", label: "邮件跟踪", icon: Mail },
  { id: "programs", label: "项目与文书", icon: BookOpenText },
  { id: "settings", label: "设置与导入", icon: Settings2 },
];

const statusOptions = ["待调研", "待发送", "已发送", "已跟进", "一般回复", "积极回复", "约谈", "已申请", "Offer", "婉拒", "暂停"];
const eventOptions = [
  ["sent", "已发送邮件"],
  ["follow_up", "已发送跟进"],
  ["reply_positive", "收到积极回复"],
  ["reply_neutral", "收到一般回复"],
  ["reply_negative", "收到婉拒"],
  ["meeting", "确定约谈"],
  ["application", "已提交申请"],
  ["offer", "收到 Offer"],
  ["note", "补充备注"],
];

const emptyAdvisor: Advisor = {
  id: "",
  tier: "主申",
  batch: "待安排",
  institution: "",
  name: "",
  favorite: false,
  title: "",
  email: "",
  status: "待调研",
  profileUrl: "",
  plannedSendDate: "",
  actualSentDate: "",
  nextFollowUp: "",
  replyStatus: "",
  notes: "",
  coreDirection: "",
  recentWork: "",
  keyMethods: "",
  keyFinding: "",
  fit: "",
  extensionQuestion: "",
  platformAdvantage: "",
  proposedQuestion: "",
  personalization: "",
  riskNote: "",
  sourceUrl: "",
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  const payload = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || `请求失败（${response.status}）`);
  return payload;
}

function shortDate(value?: string) {
  if (!value) return "—";
  return value.slice(0, 10);
}

function statusClass(status: string) {
  if (["Offer", "积极回复", "约谈"].includes(status)) return "success";
  if (["婉拒", "暂停"].includes(status)) return "danger";
  if (["已发送", "已跟进", "一般回复", "已申请"].includes(status)) return "info";
  return "neutral";
}

function tierClass(tier: string) {
  if (tier === "冲刺") return "reach";
  if (tier === "主申") return "main";
  return "safe";
}

function friendlyEvent(type: string) {
  return eventOptions.find(([value]) => value === type)?.[1] || "记录更新";
}

function Toast({ message }: { message: string }) {
  return <div className="toast"><Check size={16} />{message}</div>;
}

function Login({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand-mark"><Sparkles size={22} /></div>
        <p className="eyebrow">YUMO · 2027 RESEARCH</p>
        <h1>申请工作台</h1>
        <p className="muted">导师、套磁信与联系进度集中管理。</p>
        <form onSubmit={submit}>
          <label>访问密码</label>
          <div className="input-with-icon"><LockKeyhole size={17} /><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="输入服务器访问密码" /></div>
          {error && <p className="form-error">{error}</p>}
          <button className="button primary wide" disabled={loading}>{loading ? <LoaderCircle className="spin" size={17} /> : <ArrowUpRight size={17} />}进入工作台</button>
        </form>
      </section>
    </main>
  );
}

export default function App() {
  const [view, setView] = useState<View>(window.location.hash === "#programs" ? "programs" : "dashboard");
  const [programDirty, setProgramDirty] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [data, setData] = useState<AppData>({ advisors: [], letters: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [query, setQuery] = useState("");
  const [advisorDraft, setAdvisorDraft] = useState<Advisor | null>(null);
  const [newAdvisor, setNewAdvisor] = useState(false);
  const [eventAdvisor, setEventAdvisor] = useState<Advisor | null>(null);
  const [activeAdvisorId, setActiveAdvisorId] = useState(() => window.localStorage.getItem("applylab.activeAdvisor") || "");
  const [savingFavoriteId, setSavingFavoriteId] = useState("");

  function navigate(next: View) {
    if (view === "programs" && next !== "programs" && programDirty) {
      if (!window.confirm("项目与文书中有未保存的修改。确定离开？")) return;
      setProgramDirty(false);
    }
    if (view === "advisors") window.sessionStorage.setItem("applylab.advisorScroll", String(window.scrollY));
    setView(next);
    window.history.replaceState(null, "", next === "programs" ? "#programs" : window.location.pathname);
    setMobileNav(false);
    if (next === "advisors") {
      window.requestAnimationFrame(() => window.scrollTo({ top: Number(window.sessionStorage.getItem("applylab.advisorScroll") || 0) }));
    }
  }

  function startLetter(advisor: Advisor) {
    setActiveAdvisorId(advisor.id);
    window.localStorage.setItem("applylab.activeAdvisor", advisor.id);
    navigate("letters");
  }

  async function updateAdvisorStatus(advisor: Advisor, status: string) {
    await api(`/api/advisors/${encodeURIComponent(advisor.id)}`, { method: "PATCH", body: JSON.stringify({ status }) });
    await load();
    setToast(status === "暂停" ? "已暂缓这位导师" : "已加入套磁队列");
  }

  async function toggleAdvisorFavorite(advisor: Advisor) {
    const favorite = !advisor.favorite;
    setSavingFavoriteId(advisor.id);
    setData((current) => ({
      ...current,
      advisors: current.advisors.map((item) => item.id === advisor.id ? { ...item, favorite } : item),
    }));
    try {
      await api(`/api/advisors/${encodeURIComponent(advisor.id)}`, { method: "PATCH", body: JSON.stringify({ favorite }) });
      await load();
      setToast(favorite ? "已加入星标导师" : "已取消星标");
    } catch (err) {
      await load();
      setError(err instanceof Error ? err.message : "星标保存失败");
    } finally {
      setSavingFavoriteId("");
    }
  }

  async function load() {
    setLoading(true);
    try {
      const fresh = await api<AppData>("/api/data");
      setData(fresh);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "数据读取失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api<{ authenticated: boolean }>("/api/auth/status")
      .then((result) => {
        setAuthenticated(result.authenticated);
        if (result.authenticated) return load();
      })
      .catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const modelContext = (document as Document & { modelContext?: { registerTool?: (tool: object, options?: { signal?: AbortSignal }) => void | Promise<void> } }).modelContext;
    if (!modelContext?.registerTool || !authenticated) return;
    const lifecycle = new AbortController();
    const register = (tool: object) => Promise.resolve(modelContext.registerTool?.(tool, { signal: lifecycle.signal })).catch(() => undefined);
    void register({
      name: "apply_crm_search_advisors_v1",
      title: "检索申请导师",
      description: "按姓名、学校或研究方向检索当前申请工作台中的导师，返回精简结果。",
      inputSchema: { type: "object", properties: { query: { type: "string" } }, required: ["query"], additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: ({ query: search }: { query: string }) => data.advisors
        .filter((advisor) => [advisor.name, advisor.institution, advisor.coreDirection].join(" ").toLowerCase().includes(String(search).toLowerCase()))
        .slice(0, 10)
        .map(({ id, name, institution, tier, status, coreDirection }) => ({ id, name, institution, tier, status, coreDirection })),
    });
    void register({
      name: "apply_crm_update_advisor_status_v1",
      title: "更新导师申请状态",
      description: "更新指定导师的申请状态，并同步刷新工作台。",
      inputSchema: {
        type: "object",
        properties: { advisorId: { type: "string" }, status: { type: "string", enum: statusOptions } },
        required: ["advisorId", "status"], additionalProperties: false,
      },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      execute: async ({ advisorId, status }: { advisorId: string; status: string }) => {
        if (!data.advisors.some((advisor) => advisor.id === advisorId)) throw new Error("导师不存在");
        await api(`/api/advisors/${encodeURIComponent(advisorId)}`, { method: "PATCH", body: JSON.stringify({ status }) });
        await load();
        return { advisorId, status, updated: true };
      },
    });
    return () => lifecycle.abort();
  }, [authenticated, data.advisors]);

  const searchResults = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return data.advisors.filter((advisor) => [advisor.name, advisor.institution, advisor.coreDirection, advisor.recentWork].join(" ").toLowerCase().includes(needle)).slice(0, 6);
  }, [data.advisors, query]);

  async function saveAdvisor() {
    if (!advisorDraft?.name.trim()) return setError("请填写导师姓名");
    try {
      if (newAdvisor) {
        await api("/api/advisors", { method: "POST", body: JSON.stringify(advisorDraft) });
      } else {
        await api(`/api/advisors/${encodeURIComponent(advisorDraft.id)}`, { method: "PATCH", body: JSON.stringify(advisorDraft) });
      }
      setAdvisorDraft(null);
      setNewAdvisor(false);
      await load();
      setToast("导师资料已保存");
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    }
  }

  async function removeAdvisor(advisor: Advisor) {
    if (!window.confirm(`删除 ${advisor.name} 以及关联信件和联系记录？`)) return;
    await api(`/api/advisors/${encodeURIComponent(advisor.id)}`, { method: "DELETE" });
    setAdvisorDraft(null);
    await load();
    setToast("导师记录已删除");
  }

  async function logout() {
    if (view === "programs" && programDirty && !window.confirm("项目与文书中有未保存的修改。确定退出？")) return;
    await api("/api/auth/logout", { method: "POST" });
    setAuthenticated(false);
  }

  if (authenticated === null) return <main className="loading-screen"><LoaderCircle className="spin" />正在打开申请工作台…</main>;
  if (!authenticated) return <Login onDone={() => { setAuthenticated(true); void load(); }} />;

  const activeLabel = navItems.find((item) => item.id === view)?.label;

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "mobile-open" : ""}`}>
        <div className="brand-row"><div className="brand-mark small"><Sparkles size={17} /></div><div><strong>申请工作台</strong><span>Yumo · 2027 Research</span></div></div>
        <nav>
          {navItems.map((item) => {
            const Icon = item.icon;
            return <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => navigate(item.id)}><Icon size={18} />{item.label}</button>;
          })}
        </nav>
        <div className="sidebar-bottom">
          <div className="profile-dot">陈</div><div><strong>陈禹墨</strong><span>临床医学 · HUST</span></div>
          <button className="icon-button dark" title="退出" onClick={logout}><LogOut size={16} /></button>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileNav(!mobileNav)}><Menu size={20} /></button>
          <div className="breadcrumb"><span>申请工作台</span><ChevronRight size={14} /><strong>{activeLabel}</strong></div>
          <div className="global-search">
            <Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索导师、学校或方向" />
            {query && <button aria-label="清除搜索" onClick={() => setQuery("")}><X size={15} /></button>}
            {query && <div className="search-popover">
              {searchResults.length ? searchResults.map((advisor) => <button key={advisor.id} onClick={() => { setAdvisorDraft(advisor); setNewAdvisor(false); setQuery(""); }}><strong>{advisor.name}</strong><span>{advisor.institution} · {advisor.coreDirection}</span></button>) : <p>没有匹配结果</p>}
            </div>}
          </div>
          <button className="button secondary compact" onClick={() => navigate("settings")}><Upload size={16} />导入更新</button>
          <button className="button primary compact" onClick={() => { setAdvisorDraft({ ...emptyAdvisor }); setNewAdvisor(true); }}><Plus size={16} />新增导师</button>
        </header>

        <main className="content">
          {error && <div className="error-banner"><CircleAlert size={17} /><span>{error}</span><button onClick={() => setError("")}><X size={16} /></button></div>}
          {loading && data.advisors.length === 0 ? <div className="page-loader"><LoaderCircle className="spin" />正在读取数据…</div> : null}
          {view === "dashboard" && <Dashboard data={data} onOpenAdvisor={(advisor) => { setAdvisorDraft(advisor); setNewAdvisor(false); }} onGo={navigate} onEvent={setEventAdvisor} onStartLetter={startLetter} />}
          {view === "advisors" && <AdvisorsView advisors={data.advisors} onOpen={(advisor) => { setAdvisorDraft(advisor); setNewAdvisor(false); }} onAdd={() => { setAdvisorDraft({ ...emptyAdvisor }); setNewAdvisor(true); }} onStartLetter={startLetter} onStatus={updateAdvisorStatus} onFavorite={toggleAdvisorFavorite} savingFavoriteId={savingFavoriteId} />}
          {view === "letters" && <LettersView advisors={data.advisors} letters={data.letters} initialAdvisorId={activeAdvisorId} onSelected={(id) => { setActiveAdvisorId(id); window.localStorage.setItem("applylab.activeAdvisor", id); }} onBack={() => navigate("advisors")} onOpenAdvisor={(advisor) => { setAdvisorDraft(advisor); setNewAdvisor(false); }} onSaved={load} notify={setToast} />}
          {view === "tracking" && <TrackingView advisors={data.advisors} events={data.events} onEvent={setEventAdvisor} onOpen={(advisor) => { setAdvisorDraft(advisor); setNewAdvisor(false); }} onStartLetter={startLetter} />}
          {view === "programs" && data.programs !== undefined && <ProgramsView programs={data.programs} advisors={data.advisors} onDirtyChange={setProgramDirty} onSaved={(program) => setData((current) => ({ ...current, programs: [...(current.programs || []).filter((p) => p.id !== program.id), program] }))} onOpenAdvisor={(id) => { const advisor = data.advisors.find((a) => a.id === id); if (advisor) { setAdvisorDraft(advisor); setNewAdvisor(false); } }} />}
          {view === "settings" && <SettingsView data={data} onImported={load} notify={setToast} />}
        </main>
      </div>

      {advisorDraft && <AdvisorDrawer advisor={advisorDraft} isNew={newAdvisor} onChange={setAdvisorDraft} onClose={() => { setAdvisorDraft(null); setNewAdvisor(false); }} onSave={saveAdvisor} onDelete={() => removeAdvisor(advisorDraft)} />}
      {eventAdvisor && <EventDialog advisor={eventAdvisor} onClose={() => setEventAdvisor(null)} onSaved={async () => { setEventAdvisor(null); await load(); setToast("联系记录已更新"); }} />}
      {toast && <Toast message={toast} />}
    </div>
  );
}

function Dashboard({ data, onOpenAdvisor, onGo, onEvent, onStartLetter }: { data: AppData; onOpenAdvisor: (advisor: Advisor) => void; onGo: (view: View) => void; onEvent: (advisor: Advisor) => void; onStartLetter: (advisor: Advisor) => void }) {
  const nowDate = new Date();
  const today = nowDate.toISOString().slice(0, 10);
  const todayLabel = new Intl.DateTimeFormat("zh-CN", { weekday: "long", year: "numeric", month: "short", day: "numeric" }).format(nowDate);
  const waiting = data.advisors.filter((advisor) => ["待发送", "待调研"].includes(advisor.status));
  const due = data.advisors.filter((advisor) => advisor.nextFollowUp && advisor.nextFollowUp <= today && !["积极回复", "约谈", "Offer", "婉拒", "暂停"].includes(advisor.status));
  const positive = data.advisors.filter((advisor) => ["积极回复", "约谈", "Offer"].includes(advisor.status));
  const nextBatch = [...data.advisors].filter((advisor) => advisor.status === "待发送").sort((a, b) => a.plannedSendDate.localeCompare(b.plannedSendDate)).slice(0, 5);
  const nearest = nextBatch[0];
  const letter = data.letters.find((item) => item.advisorId === nearest?.id);

  return <>
    <section className="page-heading"><div><p className="eyebrow">{todayLabel}</p><h1>今天该推进什么？</h1><p>先完成第一批个性化检查，再按计划发送并留下可跟进记录。</p></div><button className="button secondary" onClick={() => onGo("tracking")}><ListChecks size={17} />查看本周任务</button></section>
    <section className="metric-grid">
      <Metric icon={UserRoundSearch} label="导师池" value={data.advisors.length} note="港新英美欧 · 已完成初筛" tone="navy" />
      <Metric icon={Send} label="待发送" value={waiting.length} note="三批次依次测试回复" tone="teal" />
      <Metric icon={CalendarClock} label="到期跟进" value={due.length} note={due.length ? "今天需要处理" : "发送后第8天提醒"} tone="amber" />
      <Metric icon={MessageSquareText} label="积极进展" value={positive.length} note="积极回复 / 约谈 / Offer" tone="mint" />
    </section>
    <section className="dashboard-grid">
      <div className="panel span-two">
        <div className="panel-header"><div><p className="eyebrow">NEXT BATCH</p><h2>下一批发送名单</h2></div><button className="text-button" onClick={() => onGo("advisors")}>查看全部<ArrowUpRight size={15} /></button></div>
        <div className="table-wrap"><table><thead><tr><th>导师</th><th>学校</th><th>档位</th><th>计划发送</th><th>状态</th><th>操作</th></tr></thead><tbody>
          {nextBatch.map((advisor) => <tr key={advisor.id}><td><button className="name-link" onClick={() => onOpenAdvisor(advisor)}>{advisor.name}</button><span className="subline">{advisor.title}</span></td><td>{advisor.institution}</td><td><span className={`tier ${tierClass(advisor.tier)}`}>{advisor.tier}</span></td><td>{shortDate(advisor.plannedSendDate)}</td><td><span className={`status ${statusClass(advisor.status)}`}>{advisor.status}</span></td><td><div className="button-row"><button className="icon-button" aria-label={`编辑 ${advisor.name} 的套磁信`} title="直接写信" onClick={() => onStartLetter(advisor)}><FileText size={17} /></button><button className="icon-button" aria-label={`记录 ${advisor.name} 的联系进展`} title="记录联系" onClick={() => onEvent(advisor)}><MoreHorizontal size={18} /></button></div></td></tr>)}
        </tbody></table></div>
      </div>
      <div className="stack">
        <div className="panel action-panel"><div className="panel-header"><div><p className="eyebrow">PRIORITY</p><h2>当前关键动作</h2></div></div>
          <button className="action-row" onClick={() => onGo("letters")}><span className="action-icon coral"><PencilLine size={17} /></span><span><strong>检查近期批次套磁信</strong><small>论文题目、科学问题、称谓</small></span><ChevronRight size={16} /></button>
          <button className="action-row" onClick={() => onGo("advisors")}><span className="action-icon violet"><BookOpenText size={17} /></span><span><strong>补充最新导师动态</strong><small>招聘信号、资金与项目来源</small></span><ChevronRight size={16} /></button>
          <button className="action-row" onClick={() => onGo("tracking")}><span className="action-icon mint"><CalendarClock size={17} /></span><span><strong>发送后自动排跟进</strong><small>第8天提醒，只跟进一次</small></span><ChevronRight size={16} /></button>
        </div>
        {nearest && <div className="panel next-letter"><div className="panel-header"><div><p className="eyebrow">NEXT LETTER</p><h2>{nearest.name}</h2></div><span className={`tier ${tierClass(nearest.tier)}`}>{nearest.tier}</span></div><p className="clamp">{nearest.personalization}</p><div className="letter-footer"><span><FileText size={14} />{letter?.status || "未建稿"} · v{letter?.version || 0}</span><button className="button primary compact" onClick={() => onStartLetter(nearest)}>继续编辑</button></div></div>}
      </div>
    </section>
  </>;
}

function Metric({ icon: Icon, label, value, note, tone }: { icon: typeof Mail; label: string; value: number; note: string; tone: string }) {
  return <div className="metric-card"><div className={`metric-icon ${tone}`}><Icon size={20} /></div><div><p>{label}</p><strong>{value}</strong><span>{note}</span></div></div>;
}

function AdvisorsView({ advisors, onOpen, onAdd, onStartLetter, onStatus, onFavorite, savingFavoriteId }: { advisors: Advisor[]; onOpen: (advisor: Advisor) => void; onAdd: () => void; onStartLetter: (advisor: Advisor) => void; onStatus: (advisor: Advisor, status: string) => Promise<void>; onFavorite: (advisor: Advisor) => Promise<void>; savingFavoriteId: string }) {
  const [filter, setFilter] = useState(() => window.localStorage.getItem("applylab.advisorFilter") || "");
  const [tier, setTier] = useState(() => window.localStorage.getItem("applylab.advisorTier") || "全部档位");
  const [school, setSchool] = useState(() => window.localStorage.getItem("applylab.advisorSchool") || "全部学校");
  const [status, setStatus] = useState(() => window.localStorage.getItem("applylab.advisorStatus") || "全部状态");
  const [favoritesOnly, setFavoritesOnly] = useState(() => window.localStorage.getItem("applylab.advisorFavoritesOnly") === "true");
  const [expandedId, setExpandedId] = useState(() => window.localStorage.getItem("applylab.expandedAdvisor") || "");
  const schools = [...new Set(advisors.map((advisor) => advisor.institution))];
  const favoriteCount = advisors.filter((advisor) => advisor.favorite).length;
  const rows = advisors
    .filter((advisor) => {
      const text = [advisor.name, advisor.institution, advisor.coreDirection, advisor.recentWork, advisor.fit, advisor.batch].join(" ").toLowerCase();
      return (!favoritesOnly || advisor.favorite) && (!filter || text.includes(filter.toLowerCase())) && (tier === "全部档位" || advisor.tier === tier) && (school === "全部学校" || advisor.institution === school) && (status === "全部状态" || advisor.status === status);
    })
    .sort((a, b) => (a.plannedSendDate || "9999").localeCompare(b.plannedSendDate || "9999"));

  useEffect(() => { window.localStorage.setItem("applylab.advisorFilter", filter); }, [filter]);
  useEffect(() => { window.localStorage.setItem("applylab.advisorTier", tier); }, [tier]);
  useEffect(() => { window.localStorage.setItem("applylab.advisorSchool", school); }, [school]);
  useEffect(() => { window.localStorage.setItem("applylab.advisorStatus", status); }, [status]);
  useEffect(() => { window.localStorage.setItem("applylab.advisorFavoritesOnly", String(favoritesOnly)); }, [favoritesOnly]);

  function toggle(id: string) {
    const next = expandedId === id ? "" : id;
    setExpandedId(next);
    window.localStorage.setItem("applylab.expandedAdvisor", next);
  }

  return <>
    <section className="page-heading"><div><p className="eyebrow">DISCOVER → DECIDE → CONTACT</p><h1>先看导师，再决定是否套磁</h1><p>展开一位导师即可核对研究、匹配度和风险；决定后直接进入他的中文套磁信。</p></div><button className="button primary" onClick={onAdd}><Plus size={17} />新增导师</button></section>
    <div className="panel advisor-browser">
      <div className="toolbar advisor-toolbar"><div className="local-search"><Search size={16} /><input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="姓名、疾病、方法或论文" /></div><Filter size={15} className="muted-icon" /><select value={tier} onChange={(event) => setTier(event.target.value)}><option>全部档位</option><option>冲刺</option><option>主申</option><option>相对保底</option></select><select value={status} onChange={(event) => setStatus(event.target.value)}><option>全部状态</option>{statusOptions.map((item) => <option key={item}>{item}</option>)}</select><select value={school} onChange={(event) => setSchool(event.target.value)}><option>全部学校</option>{schools.map((item) => <option key={item}>{item}</option>)}</select></div>
      <div className="result-bar"><span>当前显示 <strong>{rows.length}</strong> 位导师</span><div className="result-actions"><button className={`favorite-filter ${favoritesOnly ? "active" : ""}`} onClick={() => setFavoritesOnly(!favoritesOnly)} aria-pressed={favoritesOnly}><Star size={15} />只看星标 <strong>{favoriteCount}</strong></button>{expandedId && <button className="text-button" onClick={() => toggle(expandedId)}>全部折叠<ChevronDown size={14} /></button>}</div></div>
      <div className="advisor-card-list">
        {rows.map((advisor) => {
          const expanded = expandedId === advisor.id;
          return <article key={advisor.id} className={`advisor-card ${expanded ? "expanded" : ""}`}>
            <div className="advisor-card-head"><button className="advisor-card-summary" onClick={() => toggle(advisor.id)} aria-expanded={expanded}>
              <span className="advisor-card-identity"><span className="avatar">{advisor.name.slice(0, 1)}</span><span><strong>{advisor.name}</strong><small>{advisor.institution} · {advisor.title}</small></span></span>
              <span className="advisor-card-direction"><small>研究核心</small>{advisor.coreDirection}</span>
              <span className="advisor-card-meta"><span className={`tier ${tierClass(advisor.tier)}`}>{advisor.tier}</span><span className={`status ${statusClass(advisor.status)}`}>{advisor.status}</span><ChevronDown className="expand-icon" size={17} /></span>
            </button><button className={`advisor-star ${advisor.favorite ? "active" : ""}`} onClick={() => void onFavorite(advisor)} aria-label={`${advisor.favorite ? "取消星标" : "星标"} ${advisor.name}`} aria-pressed={Boolean(advisor.favorite)} title={advisor.favorite ? "取消星标" : "加入星标"} disabled={savingFavoriteId === advisor.id}><Star size={19} /></button></div>
            {expanded && <div className="advisor-card-detail">
              <div className="advisor-insight-grid">
                <section><p className="detail-kicker">他在做什么</p><h3>近期工作与方法</h3><p>{advisor.recentWork}</p><div className="method-strip">{advisor.keyMethods}</div><p><strong>核心发现：</strong>{advisor.keyFinding}</p></section>
                <section><p className="detail-kicker">为什么值得联系</p><h3>你的切入点</h3><p>{advisor.fit}</p><p><strong>可以追问：</strong>{advisor.extensionQuestion}</p><p><strong>拟研究问题：</strong>{advisor.proposedQuestion}</p></section>
              </div>
              <details className="risk-fold"><summary>平台、资金与使用提醒</summary><div><p><strong>平台：</strong>{advisor.platformAdvantage}</p><p><strong>风险：</strong>{advisor.riskNote}</p><p><strong>内部备注：</strong>{advisor.notes || "暂无"}</p></div></details>
              <div className="advisor-actions"><div className="button-row">{advisor.profileUrl && <a className="button secondary compact" href={advisor.profileUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} />导师官网</a>}<button className="button secondary compact" onClick={() => onOpen(advisor)}><PencilLine size={15} />编辑资料</button></div><div className="button-row">{advisor.status !== "暂停" && <button className="button quiet compact" onClick={() => void onStatus(advisor, "暂停")}>暂不联系</button>}<button className="button primary compact" onClick={async () => { if (advisor.status === "待调研" || advisor.status === "暂停") await onStatus(advisor, "待发送"); onStartLetter(advisor); }}><FileText size={15} />{advisor.status === "待调研" || advisor.status === "暂停" ? "列入套磁并写信" : "进入这位导师的套磁信"}<ChevronRight size={15} /></button></div></div>
            </div>}
          </article>;
        })}
        {rows.length === 0 && <div className="empty-state">没有符合当前筛选条件的导师。</div>}
      </div>
    </div>
  </>;
}

function LettersView({ advisors, letters, initialAdvisorId, onSelected, onBack, onOpenAdvisor, onSaved, notify }: { advisors: Advisor[]; letters: Letter[]; initialAdvisorId: string; onSelected: (id: string) => void; onBack: () => void; onOpenAdvisor: (advisor: Advisor) => void; onSaved: () => Promise<void>; notify: (message: string) => void }) {
  const firstId = advisors.some((advisor) => advisor.id === initialAdvisorId) ? initialAdvisorId : advisors[0]?.id || "";
  const firstLetter = letters.find((letter) => letter.advisorId === firstId) || { advisorId: firstId, chineseSubject: "", chineseBody: "", subject: "", body: "", status: "草稿", version: 1 };
  const [selectedId, setSelectedId] = useState(firstId);
  const [draft, setDraft] = useState<Letter | null>(firstLetter);
  const [savedDraft, setSavedDraft] = useState<Letter | null>(firstLetter);
  const [language, setLanguage] = useState<"zh" | "en">("zh");
  const [search, setSearch] = useState("");
  const [listCollapsed, setListCollapsed] = useState(() => window.localStorage.getItem("applylab.letterListCollapsed") === "true");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [markingSent, setMarkingSent] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiResult, setAiResult] = useState<AiLetterResult | null>(null);
  const [aiError, setAiError] = useState("");
  const selected = advisors.find((advisor) => advisor.id === selectedId) || advisors[0];
  const dirty = Boolean(draft && savedDraft && ["chineseSubject", "chineseBody", "subject", "body", "status"].some((key) => draft[key as keyof Letter] !== savedDraft[key as keyof Letter]));
  const placeholders = draft ? [...new Set(`${draft.chineseSubject} ${draft.chineseBody} ${draft.subject} ${draft.body}`.match(/\[[^\]]+\]/g) || [])] : [];
  const wordCount = draft?.body.trim() ? draft.body.trim().split(/\s+/).length : 0;
  const chineseCount = draft?.chineseBody.replace(/\s/g, "").length || 0;
  const filtered = advisors.filter((advisor) => [advisor.name, advisor.institution, advisor.coreDirection, advisor.status].join(" ").toLowerCase().includes(search.toLowerCase()));
  const groups = [
    { label: "待处理", statuses: ["待调研", "待发送"] },
    { label: "已联系", statuses: ["已发送", "已跟进"] },
    { label: "有回复 / 推进中", statuses: ["一般回复", "积极回复", "约谈", "已申请"] },
    { label: "已结束 / 暂停", statuses: ["Offer", "婉拒", "暂停"] },
  ];
  const queue = advisors.filter((advisor) => ["待调研", "待发送"].includes(advisor.status));
  const navigationList = queue.some((advisor) => advisor.id === selectedId) ? queue : advisors;
  const currentIndex = navigationList.findIndex((advisor) => advisor.id === selectedId);

  useEffect(() => {
    document.getElementById(`letter-person-${selectedId}`)?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  function selectAdvisor(id: string, force = false) {
    if (id === selectedId) return;
    if (!force && dirty && !window.confirm("当前套磁信还没有保存。放弃本次修改并切换导师？")) return;
    const next = letters.find((letter) => letter.advisorId === id) || { advisorId: id, chineseSubject: "", chineseBody: "", subject: "", body: "", status: "草稿", version: 1 };
    setSelectedId(id);
    setDraft(next);
    setSavedDraft(next);
    setLanguage("zh");
    setAiResult(null);
    setAiError("");
    onSelected(id);
  }

  async function persist(showMessage = true) {
    if (!selected || !draft) return null;
    setSaving(true);
    try {
      const saved = await api<Letter>(`/api/letters/${encodeURIComponent(selected.id)}`, { method: "PUT", body: JSON.stringify(draft) });
      const next = { ...draft, ...saved, updatedAt: new Date().toISOString() };
      setDraft(next);
      setSavedDraft(next);
      await onSaved();
      if (showMessage) notify("中文母稿和英文稿已一起保存");
      return next;
    } finally { setSaving(false); }
  }

  async function copy() {
    if (!draft?.subject || !draft.body) return notify("请先生成并检查英文发送稿");
    await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
    notify("英文邮件已复制");
  }

  async function generateEnglish() {
    if (!selected || !draft) return;
    setGenerating(true);
    setAiError("");
    try {
      const result = await api<AiLetterResult>("/api/ai/translate-letter", {
        method: "POST",
        body: JSON.stringify({ advisorId: selected.id, currentDraft: draft, instruction: aiInstruction }),
      });
      setDraft({ ...draft, subject: result.subject, body: result.body, status: "待复核" });
      setAiResult(result);
      setLanguage("en");
      notify("英文翻译稿已生成；中文母稿没有改变");
    } catch (error) {
      setAiError(error instanceof Error ? error.message : "DeepSeek 翻译失败");
    } finally {
      setGenerating(false);
    }
  }

  async function markSentAndNext() {
    if (!selected || !draft?.subject || !draft.body) return notify("发送前请先准备好英文稿");
    setMarkingSent(true);
    try {
      if (dirty) await persist(false);
      await api("/api/events", { method: "POST", body: JSON.stringify({ advisorId: selected.id, type: "sent", occurredAt: new Date().toISOString(), note: "已从套磁工作流确认发送", nextAction: "发送一次跟进邮件", nextActionAt: "" }) });
      await onSaved();
      const next = navigationList[currentIndex + 1] || queue.find((advisor) => advisor.id !== selected.id);
      if (next) selectAdvisor(next.id, true);
      notify(next ? `已记录发送，已切换到 ${next.name}` : "已记录发送，当前队列已完成");
    } finally { setMarkingSent(false); }
  }

  return <>
    <section className="page-heading"><div><button className="back-link" onClick={onBack}><ChevronLeft size={15} />返回导师筛选</button><p className="eyebrow">CHINESE FIRST · ENGLISH DELIVERY</p><h1>先用中文想清楚，再生成英文</h1><p>中文母稿是编辑源；DeepSeek V4 Flash只负责忠实翻译和英文表达，不反向覆盖中文。</p></div><div className="button-row"><button className="button secondary" onClick={copy}><Clipboard size={17} />复制英文</button><button className="button primary" onClick={() => void persist()} disabled={saving || !dirty}>{saving ? <LoaderCircle className="spin" size={17} /> : <Save size={17} />}{dirty ? "保存全部" : "已保存"}</button></div></section>
    <section className={`letter-layout ${listCollapsed ? "list-collapsed" : ""}`}>
      {listCollapsed ? <aside className="panel letter-rail"><button title="展开导师列表" onClick={() => { setListCollapsed(false); window.localStorage.setItem("applylab.letterListCollapsed", "false"); }}><PanelLeftOpen size={18} /><span>{navigationList.length}</span></button></aside> : <aside className="panel letter-list">
        <div className="letter-list-head"><div className="local-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索导师或学校" /></div><button className="icon-button" title="收起导师列表" onClick={() => { setListCollapsed(true); window.localStorage.setItem("applylab.letterListCollapsed", "true"); }}><PanelLeftClose size={17} /></button></div>
        <div className="letter-list-scroll">{groups.map((group) => { const items = filtered.filter((advisor) => group.statuses.includes(advisor.status)); if (!items.length) return null; return <details className="letter-group" key={group.label} ref={(node) => { if (node && !node.dataset.initialized) { node.open = items.some((advisor) => advisor.id === selectedId) || group.label === "待处理"; node.dataset.initialized = "true"; } }}><summary>{group.label}<span>{items.length}</span></summary><div>{items.map((advisor) => { const letter = letters.find((item) => item.advisorId === advisor.id); return <button id={`letter-person-${advisor.id}`} key={advisor.id} className={advisor.id === selected?.id ? "active" : ""} onClick={() => selectAdvisor(advisor.id)}><span className="avatar">{advisor.name.slice(0, 1)}</span><span><strong>{advisor.name}</strong><small>{advisor.institution}</small></span><em>{letter?.status || "未建稿"}</em></button>; })}</div></details>; })}</div>
      </aside>}
      {selected && draft ? <div className="panel letter-editor">
        <div className="editor-head"><div><div className="inline-badges"><span className={`tier ${tierClass(selected.tier)}`}>{selected.tier}</span><span className={`status ${statusClass(selected.status)}`}>{selected.status}</span></div><h2>{selected.name}</h2><p>{selected.email} · {selected.institution}</p></div><div className="button-row"><button className="button secondary compact" disabled={currentIndex <= 0} onClick={() => selectAdvisor(navigationList[currentIndex - 1]?.id)}><ChevronLeft size={15} />上一位</button><button className="button secondary compact" disabled={currentIndex < 0 || currentIndex >= navigationList.length - 1} onClick={() => selectAdvisor(navigationList[currentIndex + 1]?.id)}>下一位<ChevronRight size={15} /></button></div></div>
        <details className="advisor-context"><summary><span><BookOpenText size={15} />导师研究速览</span><button onClick={(event) => { event.preventDefault(); onOpenAdvisor(selected); }}>查看完整资料</button></summary><div><p><strong>研究：</strong>{selected.coreDirection}</p><p><strong>结合点：</strong>{selected.fit}</p><p className="risk-line"><strong>提醒：</strong>{selected.riskNote}</p></div></details>
        <div className="language-tabs"><button className={language === "zh" ? "active" : ""} onClick={() => setLanguage("zh")}><span>1</span>中文母稿<small>在这里修改内容</small></button><ChevronRight size={16} /><button className={language === "en" ? "active" : ""} onClick={() => setLanguage("en")}><span>2</span>英文发送稿<small>翻译后检查发送</small></button></div>
        {language === "zh" ? <div className="language-editor">
          <label>中文邮件主题</label><input className="full-input" value={draft.chineseSubject} onChange={(event) => setDraft({ ...draft, chineseSubject: event.target.value })} />
          <div className="split-label"><label>中文正文</label><span>{chineseCount} 字 · v{draft.version}{dirty ? " · 未保存" : ""}</span></div><textarea className="letter-textarea chinese" value={draft.chineseBody} onChange={(event) => setDraft({ ...draft, chineseBody: event.target.value })} />
          <div className="ai-compose translation"><div className="ai-compose-title"><span className="action-icon violet"><Languages size={17} /></span><span><strong>DeepSeek V4 Flash 翻译</strong><small>忠实保留事实与申请路径，生成200–240词英文</small></span></div><input value={aiInstruction} onChange={(event) => setAiInstruction(event.target.value)} placeholder="可选：例如语气再克制一些，不改变事实" /><button className="button ai-button" disabled={generating || !draft.chineseBody.trim()} onClick={generateEnglish}>{generating ? <LoaderCircle className="spin" size={16} /> : <Languages size={16} />}{generating ? "正在翻译…" : "生成英文稿"}</button></div>
        </div> : <div className="language-editor">
          {aiError && <div className="ai-message error"><CircleAlert size={15} />{aiError}</div>}
          {aiResult && <div className="ai-message success"><Check size={15} /><span>{aiResult.model} · {aiResult.promptVersion}。英文已载入但尚未保存。{aiResult.warnings.length ? ` 提醒：${aiResult.warnings.join("；")}` : ""}</span></div>}
          <label>英文邮件主题</label><input className="full-input" value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} />
          <div className="split-label"><label>英文正文</label><span>{wordCount} words · v{draft.version}{dirty ? " · 未保存" : ""}</span></div><textarea className="letter-textarea" value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
          <button className="text-button regenerate" onClick={() => setLanguage("zh")}><Languages size={14} />返回中文修改后重新翻译</button>
        </div>}
        {aiError && language === "zh" && <div className="ai-message error"><CircleAlert size={15} />{aiError}</div>}
        <div className="editor-footer"><div className={placeholders.length ? "placeholder-warning" : "placeholder-ok"}>{placeholders.length ? <><CircleAlert size={15} />仍有占位符：{placeholders.join("、")}</> : <><Check size={15} />中英文均未发现占位符</>}</div><div className="button-row"><select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}><option>草稿</option><option>待复核</option><option>定稿</option><option>已发送</option></select><a className={`button secondary compact ${!draft.subject || !draft.body ? "disabled-link" : ""}`} href={draft.subject && draft.body ? `mailto:${selected.email}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}` : undefined}><Mail size={16} />在邮件客户端打开</a><button className="button primary compact" disabled={markingSent || !draft.subject || !draft.body} onClick={markSentAndNext}>{markingSent ? <LoaderCircle className="spin" size={16} /> : <SkipForward size={16} />}已发送，下一位</button></div></div>
      </div> : <div className="panel empty-state">请选择一位导师。</div>}
    </section>
  </>;
}

function TrackingView({ advisors, events, onEvent, onOpen, onStartLetter }: { advisors: Advisor[]; events: OutreachEvent[]; onEvent: (advisor: Advisor) => void; onOpen: (advisor: Advisor) => void; onStartLetter: (advisor: Advisor) => void }) {
  const [mode, setMode] = useState<"pipeline" | "list" | "timeline">("pipeline");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("全部状态");
  const [school, setSchool] = useState("全部学校");
  const [category, setCategory] = useState("全部");
  const today = new Date().toISOString().slice(0, 10);
  const schools = [...new Set(advisors.map((advisor) => advisor.institution))];
  const stages = [
    { label: "待联系", statuses: ["待调研", "待发送"] },
    { label: "已联系", statuses: ["已发送", "已跟进"] },
    { label: "有回复", statuses: ["一般回复", "积极回复"] },
    { label: "推进中", statuses: ["约谈", "已申请"] },
    { label: "结果", statuses: ["Offer", "婉拒", "暂停"] },
  ];
  function matchesFilters(advisor: Advisor) {
    const categoryMatch = category === "全部"
      || (category === "到期跟进" && Boolean(advisor.nextFollowUp && advisor.nextFollowUp <= today && !["Offer", "婉拒", "暂停"].includes(advisor.status)))
      || (category === "待联系" && ["待调研", "待发送"].includes(advisor.status))
      || (category === "已联系" && ["已发送", "已跟进"].includes(advisor.status))
      || (category === "有回复" && ["一般回复", "积极回复", "约谈", "Offer"].includes(advisor.status));
    return (status === "全部状态" || advisor.status === status) && (school === "全部学校" || advisor.institution === school) && categoryMatch;
  }
  const needle = search.trim().toLowerCase();
  const visibleAdvisors = advisors.filter((advisor) => {
    const text = [advisor.name, advisor.institution, advisor.coreDirection, advisor.notes, advisor.batch].join(" ").toLowerCase();
    return matchesFilters(advisor) && (!needle || text.includes(needle));
  });
  const visibleEvents = events.filter((event) => {
    const advisor = advisors.find((item) => item.id === event.advisorId);
    if (!advisor || !matchesFilters(advisor)) return false;
    const text = [advisor.name, advisor.institution, advisor.coreDirection, advisor.notes, advisor.batch, event.note, event.nextAction].join(" ").toLowerCase();
    return !needle || text.includes(needle);
  });
  return <>
    <section className="page-heading"><div><p className="eyebrow">FIND → FOLLOW UP → RECORD</p><h1>邮件跟踪</h1><p>先找到目标导师，再查看当前状态、到期动作和全部往来记录。</p></div><div className="segmented"><button className={mode === "pipeline" ? "active" : ""} onClick={() => setMode("pipeline")}>状态管线</button><button className={mode === "list" ? "active" : ""} onClick={() => setMode("list")}>可搜索列表</button><button className={mode === "timeline" ? "active" : ""} onClick={() => setMode("timeline")}>时间线</button></div></section>
    <div className="panel tracking-filter"><div className="local-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索导师、学校、方向或备注" /></div><select aria-label="跟踪分类" value={category} onChange={(event) => setCategory(event.target.value)}><option>全部</option><option>到期跟进</option><option>待联系</option><option>已联系</option><option>有回复</option></select><select aria-label="联系状态" value={status} onChange={(event) => setStatus(event.target.value)}><option>全部状态</option>{statusOptions.map((item) => <option key={item}>{item}</option>)}</select><select aria-label="学校筛选" value={school} onChange={(event) => setSchool(event.target.value)}><option>全部学校</option>{schools.map((item) => <option key={item}>{item}</option>)}</select><span className="filter-count">{visibleAdvisors.length} 位</span></div>
    {mode === "pipeline" && <div className="kanban">{stages.map((stage) => { const items = visibleAdvisors.filter((advisor) => stage.statuses.includes(advisor.status)); return <section key={stage.label} className="kanban-column"><div className="kanban-head"><h2>{stage.label}</h2><span>{items.length}</span></div>{items.map((advisor) => <article key={advisor.id} className={`contact-card ${advisor.nextFollowUp && advisor.nextFollowUp <= today ? "due" : ""}`}><div className="contact-top"><span className={`tier-dot ${tierClass(advisor.tier)}`} /><button onClick={() => onOpen(advisor)}>{advisor.name}</button><span className={`status ${statusClass(advisor.status)}`}>{advisor.status}</span></div><p>{advisor.institution}</p><small>{advisor.nextFollowUp ? `下一步 · ${advisor.nextFollowUp}` : advisor.plannedSendDate ? `计划发送 · ${advisor.plannedSendDate}` : "尚未排期"}</small><div className="contact-actions"><button onClick={() => onStartLetter(advisor)}><FileText size={13} />套磁信</button><button onClick={() => onEvent(advisor)}><Plus size={13} />记录</button></div></article>)}{items.length === 0 && <div className="kanban-empty">当前筛选下暂无记录</div>}</section>; })}</div>}
    {mode === "list" && <div className="panel tracking-table"><div className="table-wrap"><table><thead><tr><th>导师</th><th>当前状态</th><th>计划 / 跟进</th><th>回复</th><th>操作</th></tr></thead><tbody>{visibleAdvisors.map((advisor) => <tr key={advisor.id}><td><button className="name-link" onClick={() => onOpen(advisor)}>{advisor.name}</button><span className="subline">{advisor.institution}</span></td><td><span className={`status ${statusClass(advisor.status)}`}>{advisor.status}</span></td><td><strong className={advisor.nextFollowUp && advisor.nextFollowUp <= today ? "date-due" : ""}>{advisor.nextFollowUp || advisor.plannedSendDate || "未排期"}</strong><span className="subline">{advisor.nextFollowUp ? "下一次跟进" : "计划发送"}</span></td><td>{advisor.replyStatus || "—"}</td><td><div className="button-row"><button aria-label={`打开 ${advisor.name} 的套磁信`} className="button secondary compact" onClick={() => onStartLetter(advisor)}><FileText size={14} />套磁信</button><button aria-label={`记录 ${advisor.name} 的联系进展`} className="button primary compact" onClick={() => onEvent(advisor)}><Plus size={14} />记录进展</button></div></td></tr>)}</tbody></table>{!visibleAdvisors.length && <div className="empty-state">没有符合筛选条件的导师。</div>}</div></div>}
    {mode === "timeline" && <div className="panel timeline"><div className="timeline-list">{visibleEvents.length ? visibleEvents.map((event) => { const advisor = advisors.find((item) => item.id === event.advisorId); return <div className="timeline-item" key={event.id}><span className="timeline-dot" /><time>{shortDate(event.occurredAt)}</time><div><button className="timeline-title" onClick={() => advisor && onOpen(advisor)}>{advisor?.name || "已删除导师"} · {friendlyEvent(event.type)}</button><p>{event.note || "没有补充备注"}</p>{event.nextActionAt && <small>下一步：{event.nextAction || "待处理"} · {event.nextActionAt}</small>}</div></div>; }) : <div className="empty-state">当前筛选下没有联系记录。</div>}</div></div>}
  </>;
}

function SettingsView({ data, onImported, notify }: { data: AppData; onImported: () => Promise<void>; notify: (message: string) => void }) {
  const [mode, setMode] = useState("upsert");
  const [busy, setBusy] = useState(false);
  const [deepseek, setDeepseek] = useState<{ configured: boolean; model: string; promptVersion: string } | null>(null);
  useEffect(() => { void api<{ configured: boolean; model: string; promptVersion: string }>("/api/ai/status").then(setDeepseek).catch(() => setDeepseek(null)); }, []);
  async function importFile(file?: File) {
    if (!file) return;
    if (mode === "replace" && !window.confirm("全量替换会删除系统中不在导入文件里的记录，确定继续？")) return;
    setBusy(true);
    try {
      const payload = JSON.parse(await file.text());
      payload.mode = mode;
      await api("/api/import", { method: "POST", body: JSON.stringify(payload) });
      await onImported();
      notify("数据已导入并完成关联更新");
    } finally { setBusy(false); }
  }
  const apiSample = `curl -X POST https://你的域名/api/agent-sync \\\n  -H "Authorization: Bearer $AGENT_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  --data @update.json`;
  return <>
    <section className="page-heading"><div><p className="eyebrow">DATA & ACCESS</p><h1>设置与导入</h1><p>完整备份、批量更新以及给可信自动化使用的受保护接口。</p></div><a className="button secondary" href="/api/export"><Download size={17} />导出完整备份</a></section>
    <section className="settings-grid">
      <div className="panel settings-card"><div className="settings-icon violet"><Languages /></div><h2>DeepSeek 中文转英文</h2><p>密钥只保存在服务器。中文母稿由你掌控，V4 Flash只做忠实翻译与英文润色，不自动增删科研事实，也不会覆盖中文。</p><div className={`integration-status ${deepseek?.configured ? "ready" : "pending"}`}><span />{deepseek?.configured ? "已配置，可以翻译" : "等待配置 DEEPSEEK_API_KEY"}</div><div className="integration-meta"><span>模型</span><strong>{deepseek?.model || "deepseek-v4-flash"}</strong></div><div className="integration-meta"><span>提示词</span><strong>{deepseek?.promptVersion || "outreach-zh-en-v2.0"}</strong></div><a className="source-link docs-link" href="https://api-docs.deepseek.com/quick_start/pricing" target="_blank" rel="noreferrer"><ExternalLink size={14} />DeepSeek V4 Flash 官方说明</a></div>
      <div className="panel settings-card"><div className="settings-icon"><Upload /></div><h2>上传更新</h2><p>支持本系统导出的 JSON。默认“合并更新”只覆盖同 ID 记录，不删除已有数据。</p><label>导入模式</label><select value={mode} onChange={(event) => setMode(event.target.value)}><option value="upsert">合并更新（推荐）</option><option value="replace">全量替换</option></select><label className={`upload-zone ${busy ? "busy" : ""}`}><Upload size={22} /><strong>{busy ? "正在导入…" : "选择 JSON 文件"}</strong><span>最大 4 MB</span><input type="file" accept="application/json,.json" disabled={busy} onChange={(event) => void importFile(event.target.files?.[0])} /></label></div>
      <div className="panel settings-card"><div className="settings-icon"><Link2 /></div><h2>助手更新接口</h2><p>后续可以由我生成更新包并调用这个入口，批量添加导师、替换信件或补充联系记录。</p><div className="security-note"><LockKeyhole size={16} /><span>接口必须使用独立的 Agent API Key；页面不会显示或保存密钥。</span></div><pre>{apiSample}</pre><button className="button secondary compact" onClick={async () => { await navigator.clipboard.writeText(apiSample); notify("接口示例已复制"); }}><Clipboard size={15} />复制示例</button></div>
      <div className="panel settings-card"><div className="settings-icon"><Database /></div><h2>当前数据</h2><div className="data-stat"><span>导师</span><strong>{data.advisors.length}</strong></div><div className="data-stat"><span>独立信件</span><strong>{data.letters.length}</strong></div><div className="data-stat"><span>联系事件</span><strong>{data.events.length}</strong></div><p className="small-note">数据库保存在服务器挂载的数据目录中，升级容器不会丢失。</p></div>
    </section>
    <section className="panel architecture"><div><p className="eyebrow">WORKFLOW</p><h2>现在按真实套磁顺序组织</h2></div><div className="relationship"><span><UserRoundSearch />筛选导师<small>展开研究与匹配依据</small></span><ChevronRight /><span><PencilLine />修改中文<small>中文母稿是内容源</small></span><ChevronRight /><span><Languages />生成英文<small>Flash忠实翻译</small></span><ChevronRight /><span><Inbox />跟踪结果<small>发送、回复、下一步</small></span></div></section>
  </>;
}

function AdvisorDrawer({ advisor, isNew, onChange, onClose, onSave, onDelete }: { advisor: Advisor; isNew: boolean; onChange: (advisor: Advisor) => void; onClose: () => void; onSave: () => void; onDelete: () => void }) {
  type AdvisorTextKey = Exclude<keyof Advisor, "favorite">;
  const fields: Array<[AdvisorTextKey, string, string?]> = [
    ["name", "导师姓名"], ["institution", "学校"], ["title", "职称 / 单位"], ["email", "邮箱", "email"], ["profileUrl", "官方主页", "url"], ["plannedSendDate", "计划发送", "date"],
  ];
  const sections: Array<{ title: string; hint: string; fields: Array<[AdvisorTextKey, string]> }> = [
    { title: "研究内容", hint: "导师具体做什么", fields: [["coreDirection", "核心方向"], ["recentWork", "近期论文 / 项目"], ["keyMethods", "关键方法"], ["keyFinding", "核心发现"]] },
    { title: "匹配与切入", hint: "为什么联系、准备聊什么", fields: [["fit", "与我的结合点"], ["extensionQuestion", "自然延伸问题"], ["proposedQuestion", "拟研究问题"], ["personalization", "套磁信定制段落"]] },
    { title: "申请判断", hint: "平台、资金和风险", fields: [["platformAdvantage", "平台 / 资金优势"], ["riskNote", "风险与使用提醒"], ["notes", "内部备注"]] },
  ];
  return <div className="overlay" onMouseDown={onClose}><aside className="drawer" onMouseDown={(event) => event.stopPropagation()}><header><div><p className="eyebrow">{isNew ? "NEW ADVISOR" : advisor.institution}</p><h2>{isNew ? "新增导师" : advisor.name}</h2></div><button className="icon-button" onClick={onClose}><X size={20} /></button></header><div className="drawer-body">
    <div className="form-grid">{fields.map(([key, label, type]) => <label key={key}>{label}<input type={type || "text"} value={advisor[key]} onChange={(event) => onChange({ ...advisor, [key]: event.target.value })} /></label>)}</div>
    <div className="form-grid triple"><label>档位<select value={advisor.tier} onChange={(event) => onChange({ ...advisor, tier: event.target.value })}><option>冲刺</option><option>主申</option><option>相对保底</option></select></label><label>批次<input value={advisor.batch} onChange={(event) => onChange({ ...advisor, batch: event.target.value })} /></label><label>状态<select value={advisor.status} onChange={(event) => onChange({ ...advisor, status: event.target.value })}>{statusOptions.map((status) => <option key={status}>{status}</option>)}</select></label></div>
    {sections.map((section) => <details className="drawer-section" key={section.title}><summary><span>{section.title}<small>{section.hint}</small></span><ChevronDown size={16} /></summary><div>{section.fields.map(([key, label]) => <label key={key}>{label}<textarea rows={key === "personalization" ? 5 : 3} value={advisor[key]} onChange={(event) => onChange({ ...advisor, [key]: event.target.value })} /></label>)}</div></details>)}
    <details className="drawer-section"><summary><span>来源链接<small>官网与证据页面</small></span><ChevronDown size={16} /></summary><div><label>资料来源<input value={advisor.sourceUrl} onChange={(event) => onChange({ ...advisor, sourceUrl: event.target.value })} /></label></div></details>
  </div><footer>{!isNew ? <button className="button danger-button" onClick={onDelete}><Trash2 size={16} />删除</button> : <span />}<div className="button-row"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" onClick={onSave}><Save size={16} />保存导师</button></div></footer></aside></div>;
}

function EventDialog({ advisor, onClose, onSaved }: { advisor: Advisor; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({ type: advisor.status === "待发送" ? "sent" : "note", occurredAt: new Date().toISOString().slice(0, 16), note: "", nextAction: "", nextActionAt: "" });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api("/api/events", { method: "POST", body: JSON.stringify({ ...form, advisorId: advisor.id, occurredAt: new Date(form.occurredAt).toISOString() }) });
      await onSaved();
    } finally { setSaving(false); }
  }
  return <div className="overlay dialog-overlay" onMouseDown={onClose}><section className="dialog" onMouseDown={(event) => event.stopPropagation()}><header><div><p className="eyebrow">OUTREACH EVENT</p><h2>记录 {advisor.name} 的进展</h2></div><button className="icon-button" onClick={onClose}><X size={19} /></button></header><div className="dialog-body"><label>发生了什么<select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>{eventOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>时间<input type="datetime-local" value={form.occurredAt} onChange={(event) => setForm({ ...form, occurredAt: event.target.value })} /></label><label>备注<textarea rows={4} value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="回复要点、导师要求或你的判断" /></label><div className="form-grid"><label>下一步<input value={form.nextAction} onChange={(event) => setForm({ ...form, nextAction: event.target.value })} placeholder="例如：准备10分钟自我介绍" /></label><label>截止日期<input type="date" value={form.nextActionAt} onChange={(event) => setForm({ ...form, nextActionAt: event.target.value })} /></label></div></div><footer><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" onClick={save} disabled={saving}>{saving ? <LoaderCircle className="spin" size={16} /> : <Save size={16} />}保存记录</button></footer></section></div>;
}
